/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type { KeyboardEvent } from 'react'
import { useSelector } from 'src/hooks/useRedux'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import SelectMultiInput from 'src/components/ts-components/SelectMultiInput'
import { useSportPositionOptions } from '../hooks/useSportPositionOptions'
import { CountryAndProvinceInput } from 'src/components/ts-components/CountryAndProvinceInput'
import { TextInput } from 'src/components/ts-components/TextInput'
import Button from 'src/components/ts-components/Button'
import {
  getSports,
  getAllGenericSportProperties,
  getGenderTypes,
} from '@sportninja/common/selectors/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  genderCategoryOptions,
  genderCategorySelectValue,
  genderIdFromSelection,
} from '@sportninja/common/features/teamFinder/genderCategory'
import {
  positionFilterOptions,
  positionIdsFromSelection,
} from '@sportninja/common/features/teamFinder/positionFilter'
import type { useTeamFinderForm } from '../hooks/useTeamFinderForm'

interface Props {
  form: ReturnType<typeof useTeamFinderForm>
  onSearch: () => void
  /** Clears the form AND the filter params in the URL. */
  onClear: () => void
  isLoading: boolean
}

const TeamFinderFilters = ({ form, onSearch, onClear, isLoading }: Props) => {
  const { filters, fieldErrors } = form

  // Positions are keyed by the sport id (the parent generic id for a generic
  // pick), so they resolve off `sportId` regardless of stats type. Empty until
  // a sport is chosen — the placeholder says so.
  const sportPositions = useSportPositionOptions(filters.sportId || null)
  const positionOptions = positionFilterOptions(sportPositions)

  // Gender Category options — the division gender categories (Coed / Men's /
  // Women's / N/A), same source as Division Settings, after an "All Genders"
  // default that sends nothing (SN-8350). Sent under the `gender_id` key.
  const genderCategories = useSelector((state: any) => getGenderTypes(state))
  const genderOptions = genderCategoryOptions(
    genderCategories,
    capitalizeWords('All Genders')
  )

  // Same sport source as the create-competition wizard: full-stats sports
  // (Redux types map) + basic-stats generic sports, grouped in the dropdown.
  const sports = useSelector((state: any) => Object.values(getSports(state)))
  const genericSports = useSelector((state: any) =>
    getAllGenericSportProperties(state)
  )

  // The parent 'generic' sport id — positions for a basic-stats pick are keyed
  // under it, so it's what we store as `sportId` for generic sports.
  const genericParentId = (
    sports.find((s: any) => s?.name?.toLowerCase().includes('generic')) as
      | { id?: string }
      | undefined
  )?.id

  const fullStatsOptions = (sports as any[])
    .map((s) => ({ value: String(s.id), label: s.name_full }))
    .filter((o) => !o.label?.toLowerCase().includes('generic'))
    .sort((a, b) => a.label.localeCompare(b.label))

  const basicStatsOptions = (genericSports ?? [])
    .map((s: any) => ({ value: String(s.id), label: s.name }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label))

  const sportOptions = [
    { label: capitalizeWords('Full Stats'), options: fullStatsOptions },
    { label: capitalizeWords('Basic Stats'), options: basicStatsOptions },
  ]

  // The dropdown shows the specific generic id when a basic-stats sport is
  // chosen (else the full-stats sport id).
  const selectedSport = filters.genericSportId || filters.sportId

  const handleSportChange = (value?: string) => {
    if (!value) {
      form.setSport('', '')
      return
    }
    const generic = basicStatsOptions.find((o: any) => o.value === value)
    if (generic) {
      // Generic pick: store the parent id as sportId (for positions) and the
      // specific id as genericSportId (sent as generic_sport_id).
      form.setSport(genericParentId ?? '', value)
    } else {
      form.setSport(value, '')
    }
  }

  // Grouped options: match only leaf options by label, never the group headers.
  const filterSportOption = (input: string, option: any) => {
    if (Array.isArray(option?.options)) {
      return false
    }
    return (option?.label ?? '')
      .toString()
      .toLowerCase()
      .includes(input.toLowerCase())
  }

  // Enable Clear only when at least one filter is set.
  const hasActiveFilters = Object.values(filters).some(Boolean)

  // The free-text filters have no picker to close, so Enter should run the
  // search rather than doing nothing.
  const handleTextFilterKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      onSearch()
    }
  }

  return (
    <div css={formStyle}>
      {/* Filters laid out as labeled columns in a horizontal wrapping row,
          matching the My Schedule filter bar. */}
      <div css={rowStyle}>
        <div css={fieldColumnStyle}>
          <SelectInput
            name='sportId'
            label={capitalizeWords('Sport')}
            value={selectedSport}
            onChange={handleSportChange}
            options={sportOptions}
            fieldErrors={fieldErrors}
            customError={{}}
            placeholder={capitalizeWords('Select sport')}
            disabled={false}
            showSearch
            filterOption={filterSportOption}
            allowClear
          />
        </div>
        <div css={fieldColumnStyle}>
          <SelectMultiInput
            name='positionIds'
            label={capitalizeWords('Position')}
            value={filters.positionIds}
            onChange={(selected) =>
              form.setPositions(
                positionIdsFromSelection(selected, sportPositions)
              )
            }
            options={positionOptions}
            fieldErrors={fieldErrors}
            customError={{}}
            placeholder={
              filters.sportId
                ? capitalizeWords('All Positions')
                : capitalizeWords('Select A Sport First')
            }
            // Sits in a row of 40px controls, and position names are short.
            fixedHeight
          />
        </div>
        <div css={fieldColumnStyle}>
          <SelectInput
            name='genderId'
            label={capitalizeWords('Gender Category')}
            value={genderCategorySelectValue(filters.genderId)}
            onChange={(value) => form.setGender(genderIdFromSelection(value))}
            options={genderOptions}
            fieldErrors={fieldErrors}
            customError={{}}
            placeholder={capitalizeWords('Select gender category')}
            disabled={false}
            allowClear={false}
          />
        </div>
        <div css={fieldColumnStyle}>
          <TextInput
            name='organization'
            label={capitalizeWords('Organization')}
            value={filters.organization}
            setValue={form.setOrganization}
            placeholder={capitalizeWords('Enter an organization name')}
            fieldErrors={fieldErrors}
            customError={{}}
            onKeyDown={handleTextFilterKeyDown}
          />
        </div>
        {/* Team opens the second row, ahead of Country. */}
        <div css={fieldColumnStyle}>
          <TextInput
            name='team'
            label={capitalizeWords('Team')}
            value={filters.team}
            setValue={form.setTeam}
            placeholder={capitalizeWords('Enter a team name')}
            fieldErrors={fieldErrors}
            customError={{}}
            onKeyDown={handleTextFilterKeyDown}
          />
        </div>
        {/* Country + State render as their own two labeled columns. */}
        <div css={countryColumnStyle}>
          <CountryAndProvinceInput
            countryValue={filters.country || undefined}
            provinceValue={filters.province || undefined}
            onCountryChange={(value, countryId) =>
              form.setCountry(value, countryId)
            }
            onProvinceChange={(value, provinceId) =>
              form.setProvince(value, provinceId)
            }
            valueMode='name'
            provinceValueMode='name'
            responsive
            allowClear
            fieldErrors={{
              country: fieldErrors.country,
              province: fieldErrors.province,
            }}
            labels={{
              country: capitalizeWords('Country'),
              // Already cased: capitalizeWords splits on spaces only, so it
              // would lowercase the 'P' after the slash.
              province: capitalizeWords('State/Province'),
            }}
          />
        </div>
        <div css={fieldColumnStyle}>
          <TextInput
            name='city'
            label={capitalizeWords('City')}
            value={filters.city}
            setValue={form.setCity}
            placeholder={capitalizeWords('Enter a city')}
            fieldErrors={fieldErrors}
            customError={{}}
            onKeyDown={handleTextFilterKeyDown}
          />
        </div>
      </div>

      <div css={buttonRowStyle}>
        <Button
          text={capitalizeWords('Clear')}
          variant='secondary'
          onClick={hasActiveFilters ? onClear : () => {}}
          style={
            hasActiveFilters ? undefined : 'opacity: 0.5; cursor: not-allowed;'
          }
        />
        <Button
          text={capitalizeWords('Search For A Team')}
          variant='primary'
          onClick={onSearch}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}

export default TeamFinderFilters

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
// Wrapping filter grid. A grid rather than a flex row so that fields left over
// on the last line keep a normal column width — with flex-grow, a single
// wrapped field (e.g. Team) stretched across the whole row.
// auto-fill (not auto-fit) preserves the empty trailing tracks that keep it so.
/* The filters occupy 8 grid tracks at 4 columns: Sport/Position/Gender/Team
   fill row 1, then the two-track Country+State pair plus City fill row 2 — two
   full rows with no ragged gap before the buttons. Fixed column counts per
   breakpoint (rather than auto-fill) keep that arithmetic predictable; the
   field ORDER is what makes the rows come out even, so adding or moving a
   field means re-checking it here. */
const rowStyle = css`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`
const fieldColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`
// Country + State is a pair, so it spans two of the grid's columns — dropping
// to one once the grid itself is a single column.
const countryColumnStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  grid-column: span 2;

  @media (max-width: 560px) {
    grid-column: span 1;
  }
`
const buttonRowStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
  max-width: 520px;
  margin-left: auto;

  /* Force both buttons to equal width regardless of label length. */
  button {
    flex: 1 1 0;
    min-width: 0;
  }
  /* Keep the button labels (esp. "SEARCH FOR A TEAM") on one line. */
  button p {
    white-space: nowrap;
  }
`
